import { useState, useEffect } from "react";
import { useToast } from "../store/ToastProvider";
import { useNavigate, useParams } from "react-router-dom";
import {
  HoldingCompaniesMetricsRequest,
  ScreenerConfigType,
  SortType,
  QueryFilterType,
  SelectableMetric,
} from "../types/ScreenerTypes";
import { CompanyMetric, ScreenerDataSourceType } from "../models/tableModels";
import { usePost } from "./usePost";
import {
  CommonDataItem,
  CommonDataRequestType,
  DistinctFundsRequest,
  FundNamesItem,
} from "../types/CommonDataRequestTypes";
import {
  buildFundsOptions,
  buildMetricsOptions,
  buildSectorOptions,
  buildCompaniesOptions,
  getDynamicColumns,
} from "../pages/Screener/utils";
import { Filter } from "../types/PageFilterTypes";
import { MESSAGES } from "../messages";
import { ROUTES } from "../routes/routeNames";
import { CompassError } from "../types/errorTypes";
import { useUpdateScreenerParams } from "./useUpdateScreenerParams";
import { getMockData } from "../pages/Screener/mockData";
import { toSlug as slugify } from "../utils/formattingUtils";
import { ColumnType } from "antd/es/table";

/**
 * Custom hook for fetching and managing screener data.
 * This hook is responsible for handling the data retrieval, state management, and error handling
 * related to a company's screening process, such as fetching company metrics, fund options, sector options,
 * and metric options. It also manages the state of various parameters used in the screener.
 *
 * @returns {ScreenerData} An object containing the screener data, loading state, error state, and functions for updating the data.
 */
export const useScreener = (): ScreenerData => {
  const { addToast } = useToast();
  const { fundId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [companyMetrics, setCompanyMetrics] = useState<CompanyMetric[]>(
    getMockData([])
  );
  const [fundOptions, setFundOptions] = useState<Filter[]>([]);
  const [sectorOptions, setSectorOptions] = useState<Filter[]>([]);
  const [metricsOptions, setMetricsOptions] = useState<SelectableMetric[]>([]);
  const [dynamicMetricColumns, setDynamicMetricsColumns] = useState<
    ColumnType<ScreenerDataSourceType>[]
  >([]);
  const [allCompanyOptions, setAllCompanyOptions] = useState<Filter[]>([]);
  const [error, setError] = useState<CompassError | null>(null);

  const {
    parameters,
    screenerTableKey,
    dispatchResetFilters,
    dispatchFundUpdate,
    dispatchFilterUpdate,
    dispatchMetricValueFiltersUpdate,
    dispatchPaginationUpdate,
    dispatchSortUpdate,
  } = useUpdateScreenerParams();

  // Fetch company metrics and configuration
  const {
    error: companyMetricsError,
    sendData: fetchCompanyMetrics,
    data: companyMetricsData,
  } = usePost<CompanyMetric[], HoldingCompaniesMetricsRequest>();

  const {
    error: companyMetricsConfigError,
    sendData: fetchCompanyMetricsConfig,
    data: companyMetricsConfigData,
  } = usePost<ScreenerConfigType, HoldingCompaniesMetricsRequest>();

  const shouldUpdateCompanies = !parameters?.filters.base_column_filters?.some(
    (filter) => filter.column_key === "name"
  );

  // Fetch fund names
  const {
    error: distinctFundsError,
    sendData: fetchDistinctFunds,
    data: distinctFundsData,
  } = usePost<FundNamesItem[], CommonDataItem<DistinctFundsRequest>>();

  // Effect to load initial data
  useEffect(() => {
    fetchDistinctFunds("GET_COMMON_DATA", {
      name: CommonDataRequestType.DISTINCT_FUNDS,
      item: { distinct_funds: null },
    });
  }, []);

  useEffect(() => {
    setIsLoading(true);
    if (parameters.filters.portfolio_index_id) {
      fetchCompanyMetricsConfig(
        "GET_SCREENER_DATA",

        { ...parameters, is_config: true }
      );
      fetchCompanyMetrics(
        "GET_SCREENER_DATA",

        parameters
      );
    }
  }, [parameters]);

  useEffect(() => {
    if (companyMetricsData) {
      setCompanyMetrics(companyMetricsData);
      setIsLoading(false);
    }
  }, [companyMetricsData]);

  useEffect(() => {
    if (distinctFundsData) {
      const fundOptions = buildFundsOptions(distinctFundsData);
      const selectedFund =
        fundOptions.find((fund) => slugify(fund.key || "") === fundId) ||
        fundOptions[0];

      setFundOptions(fundOptions);
      dispatchFundUpdate(selectedFund);
      navigate(`${ROUTES.SCREENING_TOOL}/${slugify(selectedFund.key || "")}`);
    }
  }, [distinctFundsData]);

  useEffect(() => {
    if (companyMetricsConfigData) {
      const sectorOptions = buildSectorOptions(
        companyMetricsConfigData.gics_sectors
      );
      const fetchedMetricsOptions = buildMetricsOptions(
        companyMetricsConfigData.distinct_metrics
      );
      const companiesOptions = buildCompaniesOptions(
        companyMetricsConfigData.holding_companies
      );

      setDynamicMetricsColumns(
        getDynamicColumns(
          companyMetricsConfigData.distinct_metrics,
          parameters.filters.metrics
        )
      );

      setSectorOptions(sectorOptions);
      !metricsOptions.length && setMetricsOptions(fetchedMetricsOptions);
      shouldUpdateCompanies && setAllCompanyOptions(companiesOptions);
    }
  }, [companyMetricsConfigData]);

  useEffect(() => {
    if (isLoading) {
      const selectedMetricsColumns: SelectableMetric[] = metricsOptions.filter(
        (option) => parameters.filters.metrics.includes(option.key as string)
      );
      setCompanyMetrics(getMockData(selectedMetricsColumns));
    }
  }, [isLoading]);

  useEffect(() => {
    if (
      companyMetricsError ||
      distinctFundsError ||
      companyMetricsConfigError
    ) {
      const error =
        companyMetricsError || distinctFundsError || companyMetricsConfigError;
      setError(error);
      addToast(
        error?.error_user_message ||
          MESSAGES.SCREENING_TOOL.GET_COMPANY_METRICS_ERROR,
        "error"
      );
      navigate(ROUTES.CONTROL_PANEL);
    }
  }, [companyMetricsError, companyMetricsConfigError, distinctFundsError]);

  const handleFundUpdate = (fund: Filter): void => {
    dispatchFundUpdate(fund);
    navigate(`${ROUTES.SCREENING_TOOL}/${slugify(fund.key || "")}`);
  };

  return {
    isLoading,
    companyMetricsConfigData,
    companyMetrics,
    fundOptions,
    sectorOptions,
    metricsOptions,
    dynamicMetricColumns,
    allCompanyOptions,
    dispatchFundUpdate: handleFundUpdate,
    dispatchFilterUpdate,
    dispatchMetricValueFiltersUpdate,
    dispatchPaginationUpdate,
    dispatchSortUpdate,
    dispatchResetFilters,
    parameters,
    screenerTableKey,
    error,
  };
};

type ScreenerData = {
  isLoading: boolean;
  companyMetricsConfigData: ScreenerConfigType | null;
  companyMetrics: CompanyMetric[];
  dynamicMetricColumns: ColumnType<ScreenerDataSourceType>[];
  fundOptions: Filter[];
  sectorOptions: Filter[];
  metricsOptions: SelectableMetric[];
  allCompanyOptions: Filter[];
  error: CompassError | null;
  dispatchFundUpdate: (filter: Filter) => void;
  dispatchFilterUpdate: (filter: Filter[], columnKey: string) => void;
  dispatchMetricValueFiltersUpdate: (filters: QueryFilterType[]) => void;
  dispatchPaginationUpdate: (page: number) => void;
  dispatchSortUpdate: (sort: SortType | null) => void;
  dispatchResetFilters: () => void;
  parameters: HoldingCompaniesMetricsRequest;
  screenerTableKey: number;
};
